import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Page from '../../layout/containers/Page/Page';
import pageName from './page';
import { orange, white, contiOrange } from '../../style/colors';
import toPrettyDate from '../../lib/toPrettyDate';

// API
import {
  listNotificationsApi,
  createNotificationApi,
  updateNotificationApi,
  deleteNotificationApi,
} from '../../api/endpoints';

const Container = styled.div`
  padding: 40px;
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px 0;
`;

const PageDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const Card = styled.div`
  background: ${white};
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${orange};
    box-shadow: 0 0 0 3px rgba(255, 165, 0, 0.1);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  min-height: 120px;
  resize: vertical;
  font-family: inherit;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${orange};
    box-shadow: 0 0 0 3px rgba(255, 165, 0, 0.1);
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const ToggleLabel = styled.label`
  position: relative;
  display: inline-block;
  width: 50px;
  height: 24px;
`;

const ToggleSwitch = styled.input`
  opacity: 0;
  width: 0;
  height: 0;

  &:checked + span {
    background-color: ${contiOrange};
  }

  &:checked + span:before {
    transform: translateX(26px);
  }

  &:focus + span {
    box-shadow: 0 0 0 3px rgba(255, 165, 0, 0.2);
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #d1d5db;
  transition: 0.3s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const ToggleText = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${(props) =>
    props.primary
      ? `
    background: ${orange};
    color: ${white};
    
    &:hover {
      background: #ff8c00;
      box-shadow: 0 4px 12px rgba(255, 165, 0, 0.3);
    }
  `
      : `
    background: #f3f4f6;
    color: #374151;
    
    &:hover {
      background: #e5e7eb;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NotificationsList = styled.div`
  margin-top: 32px;
`;

const NotificationItem = styled.div`
  background: ${white};
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: start;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const NotificationContent = styled.div`
  flex: 1;
`;

const NotificationTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
`;

const NotificationText = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0 0 8px 0;
  white-space: pre-line;
`;

const NotificationMeta = styled.div`
  font-size: 12px;
  color: #9ca3af;
  display: flex;
  gap: 12px;
`;

const NotificationActions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: #6b7280;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    background: #f3f4f6;
    color: #111827;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  i {
    font-size: 18px;
  }
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  ${(props) =>
    props.active
      ? `
    background: #dcfce7;
    color: #166534;
  `
      : `
    background: #fee2e2;
    color: #991b1b;
  `}
`;

const LoadingText = styled.p`
  text-align: center;
  color: #6b7280;
  margin: 0;
`;

const ErrorText = styled.p`
  text-align: center;
  color: #dc2626;
  margin: 0;
`;

const initialFormData = {
  title: '',
  content: '',
  active: true,
  visibleFrom: '',
  visibleTo: '',
};

const NotificationsPage = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await listNotificationsApi();
      setNotifications(data);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(t('errorFetchingNotifications'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);
      
      const payload = {
        title: formData.title,
        content: formData.content,
        active: formData.active,
        visibleFrom: formData.visibleFrom ? new Date(formData.visibleFrom).toISOString() : null,
        visibleTo: formData.visibleTo ? new Date(formData.visibleTo).toISOString() : null,
      };
      
      await createNotificationApi(payload);
      setFormData(initialFormData);
      await fetchNotifications();
    } catch (err) {
      console.error('Error creating notification:', err);
      setError(t('errorCreatingNotification'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDeleteNotification'))) return;
    
    try {
      setError(null);
      await deleteNotificationApi(id);
      await fetchNotifications();
    } catch (err) {
      console.error('Error deleting notification:', err);
      setError(t('errorDeletingNotification'));
    }
  };

  const handleToggleActive = async (notification) => {
    try {
      setError(null);
      await updateNotificationApi(notification._id, { active: !notification.active });
      await fetchNotifications();
    } catch (err) {
      console.error('Error updating notification:', err);
      setError(t('errorUpdatingNotification'));
    }
  };

  return (
    <Page page={pageName}>
      <Container>
        <PageHeader>
          <PageTitle>{t('globalNotifications')}</PageTitle>
          <PageDescription>
            {t('globalNotificationsDesc')}
          </PageDescription>
        </PageHeader>

        <Card>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="title">{t('title')}</Label>
              <Input
                id="title"
                name="title"
                type="text"
                value={formData.title}
                onChange={handleInputChange}
                placeholder={t('enterNotificationTitle')}
                required
                disabled={submitting}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="content">{t('message')}</Label>
              <Textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                placeholder={t('enterNotificationMessage')}
                required
                disabled={submitting}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="visibleFrom">{t('visibleFrom')}</Label>
              <Input
                id="visibleFrom"
                name="visibleFrom"
                type="datetime-local"
                value={formData.visibleFrom}
                onChange={handleInputChange}
                disabled={submitting}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="visibleTo">{t('visibleTo')}</Label>
              <Input
                id="visibleTo"
                name="visibleTo"
                type="datetime-local"
                value={formData.visibleTo}
                onChange={handleInputChange}
                disabled={submitting}
              />
            </FormGroup>

            <ToggleContainer>
              <ToggleLabel htmlFor="active">
                <ToggleSwitch
                  id="active"
                  name="active"
                  type="checkbox"
                  checked={formData.active}
                  onChange={handleInputChange}
                  disabled={submitting}
                />
                <ToggleSlider />
              </ToggleLabel>
              <ToggleText>{t('activeNotification')}</ToggleText>
            </ToggleContainer>

            <ButtonGroup>
              <Button type="button" onClick={() => setFormData(initialFormData)} disabled={submitting}>
                {t('clear')}
              </Button>
              <Button type="submit" primary disabled={submitting}>
                {submitting ? t('publishing') : t('publishNotification')}
              </Button>
            </ButtonGroup>
          </form>
        </Card>

        {error && (
          <Card style={{ marginTop: '16px' }}>
            <ErrorText>{error}</ErrorText>
          </Card>
        )}

        <NotificationsList>
          {loading ? (
            <Card>
              <LoadingText>{t('loading')}</LoadingText>
            </Card>
          ) : notifications.length === 0 ? (
            <Card>
              <p style={{ textAlign: 'center', color: '#6b7280', margin: 0 }}>
                {t('noPublishedNotifications')}
              </p>
            </Card>
          ) : (
            notifications.map((notification) => {
              const hasDateRange = notification.visibleFrom || notification.visibleTo;
              const dateRangeText = hasDateRange
                ? `${notification.visibleFrom ? toPrettyDate(notification.visibleFrom) : '—'} ${t('to')} ${notification.visibleTo ? toPrettyDate(notification.visibleTo) : '—'}`
                : t('alwaysVisible');

              return (
                <NotificationItem key={notification._id}>
                  <NotificationContent>
                    <NotificationTitle>{notification.title}</NotificationTitle>
                    <NotificationText>{notification.content}</NotificationText>
                    <NotificationMeta>
                      <span>{dateRangeText}</span>
                      <StatusBadge active={notification.active}>
                        {notification.active ? t('active') : t('inactive')}
                      </StatusBadge>
                    </NotificationMeta>
                  </NotificationContent>
                  <NotificationActions>
                    <IconButton
                      onClick={() => handleToggleActive(notification)}
                      title={notification.active ? t('deactivate') : t('activate')}
                    >
                      <i className={`mdi mdi-${notification.active ? 'eye-off' : 'eye'}`} />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(notification._id)} title={t('delete')}>
                      <i className="mdi mdi-delete" />
                    </IconButton>
                  </NotificationActions>
                </NotificationItem>
              );
            })
          )}
        </NotificationsList>
      </Container>
    </Page>
  );
};

export default NotificationsPage;
